import { Link, useLocation } from 'react-router-dom';
import { Map as MapIcon, Plus, Trophy, Network, CircleUser } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { soundSynth } from '@/utils/soundSynth';
import { useLanguage } from '@/contexts/LanguageContext';

// 사이드 탭 정의 — 좌: 맵/스킬, 우: 퀘스트/캐릭터 (중앙 기록 + 버튼 기준 대칭)
interface SideTabDef {
    to: string;
    labelKo: string;
    labelEn: string;
    Icon: LucideIcon;
}

const LEFT_TABS: SideTabDef[] = [
    { to: '/map', labelKo: '맵', labelEn: 'MAP', Icon: MapIcon },
    { to: '/skills', labelKo: '스킬', labelEn: 'SKILLS', Icon: Network },
];

const RIGHT_TABS: SideTabDef[] = [
    { to: '/achievements', labelKo: '퀘스트', labelEn: 'QUESTS', Icon: Trophy },
    { to: '/profile', labelKo: '캐릭터', labelEn: 'HERO', Icon: CircleUser },
];

export function Navigation() {
    const location = useLocation();
    const { language } = useLanguage();
    const isActive = (path: string) => location.pathname === path;

    const handleTabClick = () => {
        soundSynth.playClick();
    };

    // Premium navigation HUD styling
    const textStyle = 'text-[10px] font-black tracking-tight mt-1 relative z-10';

    const renderSideTab = ({ to, labelKo, labelEn, Icon }: SideTabDef) => {
        const active = isActive(to);
        return (
            <Link
                key={to}
                to={to}
                onClick={handleTabClick}
                className="flex-1 flex justify-center items-center relative py-1 min-h-[var(--tap-min)] min-w-[var(--tap-min)]"
            >
                <motion.div
                    whileHover={{ scale: 1.05, y: -1 }}
                    whileTap={{ scale: 0.95, y: 1 }}
                    className="flex flex-col items-center relative px-2 py-2 rounded-2xl transition-all duration-300 w-14 sm:w-16"
                    style={{
                        backgroundColor: active ? '#080505' : 'transparent',
                        border: active
                            ? '1.5px solid rgba(var(--accent-rgb), 0.45)'
                            : '1.5px solid rgba(255, 255, 255, 0.02)',
                        boxShadow: active
                            ? 'inset 0 4px 10px rgba(0, 0, 0, 0.95), 0 0 15px rgba(var(--accent-rgb), 0.25)'
                            : '0 2px 4px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.03)',
                        cursor: 'pointer'
                    }}
                >
                    <Icon
                        key={active ? 'active' : 'inactive'}
                        size={20}
                        strokeWidth={active ? 2.5 : 2}
                        className={`relative z-10 transition-colors duration-200 ${active ? 'text-[var(--accent)] animate-boop' : 'text-[var(--text-muted)]'}`}
                        style={{
                            filter: active ? 'drop-shadow(0 0 6px rgba(var(--accent-rgb), 0.75))' : 'none'
                        }}
                    />
                    <span
                        className={`${textStyle} ${active ? 'text-[var(--accent)] font-extrabold' : 'text-[var(--text-muted)]'}`}
                        style={{
                            textShadow: active ? '0 0 8px rgba(var(--accent-rgb), 0.5)' : 'none'
                        }}
                    >
                        {language === 'ko' ? labelKo : labelEn}
                    </span>
                </motion.div>
            </Link>
        );
    };

    return (
        <nav className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] w-[92%] max-w-md">
            {/* Glassmorphic Capsule Console with neon glowing accents */}
            <div
                className="px-3 py-3.5 rounded-[2.5rem] flex justify-between items-center relative transition-all duration-300"
                style={{
                    backgroundColor: 'var(--card)',
                    border: '1.5px solid rgba(255, 255, 255, 0.08)',
                    borderTopColor: 'rgba(255, 255, 255, 0.2)',
                    borderBottomColor: 'rgba(0, 0, 0, 0.6)',
                    boxShadow: '0 35px 80px rgba(0, 0, 0, 0.9), 0 15px 30px rgba(0, 0, 0, 0.7), 0 0 45px rgba(var(--accent-rgb), 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.15), inset 0 -1px 3px rgba(0, 0, 0, 0.4)',
                    opacity: 1
                }}
            >
                {/* 좌측: 맵 / 스킬 */}
                {LEFT_TABS.map(renderSideTab)}

                {/* 중앙: 운동 기록 Plus 버튼 */}
                <div className="flex-1 flex flex-col items-center justify-center relative -mt-5">
                    <Link
                        to="/workout/record"
                        onClick={handleTabClick}
                        className="group relative flex flex-col items-center justify-center min-h-[var(--tap-min)] min-w-[var(--tap-min)]"
                    >
                        <div className="relative flex items-center justify-center w-14 h-14">
                            {/* Pulse Ring */}
                            <div className="absolute inset-0 rounded-full bg-[var(--accent)]/20 animate-ping group-hover:animate-none scale-105 pointer-events-none" />
                            <motion.div
                                whileHover={{ scale: 1.1, boxShadow: '0 0 35px rgba(var(--accent-rgb), 0.6), inset 0 2px 2px rgba(255,255,255,0.4)' }}
                                whileTap={{ scale: 0.95 }}
                                className="w-full h-full bg-gradient-to-b from-[var(--accent)] to-[var(--accent-dark)] rounded-full flex items-center justify-center text-[var(--bg)] border-4 border-[#0c0e12] relative z-10 transition-all duration-300"
                                style={{
                                    boxShadow: '0 12px 28px rgba(var(--accent-rgb), 0.45), 0 4px 10px rgba(0, 0, 0, 0.4), inset 0 2.5px 2px rgba(255, 255, 255, 0.4), inset 0 -2.5px 2px rgba(0, 0, 0, 0.3)'
                                }}
                            >
                                <Plus size={28} strokeWidth={4} className="text-[var(--bg)] font-black drop-shadow-[0_1.5px_1px_rgba(255,255,255,0.3)]" />
                            </motion.div>
                        </div>
                        <span
                            className={`${textStyle} text-[var(--accent)] font-extrabold`}
                            style={{ textShadow: '0 0 8px rgba(var(--accent-rgb), 0.5)' }}
                        >
                            {language === 'ko' ? '기록' : 'LOG'}
                        </span>
                    </Link>
                </div>

                {/* 우측: 퀘스트 / 캐릭터 */}
                {RIGHT_TABS.map(renderSideTab)}
            </div>
        </nav>
    );
}
